import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useDashboardData } from './hooks/useDashboardData';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';
import { mergeReportFiltersIntoSearch } from '@/utils/reportFiltersUrl';
import { appRoutes } from '@routes';
import { SingleDatePicker } from '@/components/SingleDatePicker';
import { Dropdown, filterBarSelectStyles } from '@/components/Dropdown';
import { OverallProductivity } from './components/OverallProductivity.component';
import { KeyHighlights } from './components/KeyHighlights.component';
import { ProductivityOverTime } from './components/ProductivityOverTime.component';
import { ActivityBreakdown } from './components/ActivityBreakdown.component';
import { ActivityHeatmap } from './components/ActivityHeatmap.component';
import { TopApplications } from './components/TopApplications.component';
import { FocusVsDistraction } from './components/FocusVsDistraction.component';
import { RecentAlerts } from './components/RecentAlerts.component';
import { AttendanceSummary } from './components/AttendanceSummary.component';
import styles from './DashboardOverviewNew.module.scss';

export const DashboardOverviewNewComponent = () => {
  const {
    dateRange,
    setDateRange,
    membersOptions,
    selectedMember,
    setSelectedMember,
    isLoadingMembers,
    isFetchingStats,
    isFetchingAlerts,
    overallProductivityData,
    keyHighlightsData,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    recentAlertsData,
    productivityOverTimeData,
    chartTicks,
    activityHeatmapData,
    attendanceSummaryData,
  } = useDashboardData();

  const setNavbarSlot = useNavbarSlot();
  const location = useLocation();

  const attendanceReportSearch = useMemo(
    () =>
      mergeReportFiltersIntoSearch(location.search, {
        memberId: selectedMember?.value,
        start: dateRange.start,
        end: dateRange.end,
      }),
    [location.search, selectedMember?.value, dateRange.start, dateRange.end]
  );

  // Alerts has no date filter, so it only needs the selected member forwarded.
  const alertsReportSearch = useMemo(
    () => mergeReportFiltersIntoSearch('', { memberId: selectedMember?.value }),
    [selectedMember?.value]
  );

  const handleDateChange = useCallback(
    (dateStr) => setDateRange({ start: dateStr, end: dateStr }),
    [setDateRange]
  );

  useEffect(() => {
    setNavbarSlot(
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '220px' }}>
          <Dropdown
            options={membersOptions}
            value={selectedMember}
            onChange={setSelectedMember}
            isLoading={isLoadingMembers}
            isDisabled={isLoadingMembers}
            placeholder="Select member..."
            selectStyles={filterBarSelectStyles}
          />
        </div>
        <SingleDatePicker
          date={dateRange.start}
          onDateChange={handleDateChange}
        />
      </div>
    );
    return () => setNavbarSlot(null);
  }, [
    dateRange.start,
    membersOptions,
    selectedMember,
    isLoadingMembers,
    handleDateChange,
    setSelectedMember,
    setNavbarSlot,
  ]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.row}>
        <OverallProductivity
          data={overallProductivityData}
          isLoading={isFetchingStats}
        />
        <KeyHighlights data={keyHighlightsData} isLoading={isFetchingStats} />
      </div>

      <div className={styles.row2}>
        <ProductivityOverTime
          data={productivityOverTimeData}
          isLoading={isFetchingStats}
          chartTicks={chartTicks}
        />
        <ActivityBreakdown
          data={activityBreakdownData}
          isLoading={isFetchingStats}
          footerTo={{
            pathname: appRoutes().timeline,
            search: attendanceReportSearch,
          }}
        />
        <ActivityHeatmap
          data={activityHeatmapData}
          isLoading={isFetchingStats}
        />
      </div>

      <div className={styles.row3}>
        <TopApplications
          data={topApplicationsData}
          isLoading={isFetchingStats}
        />
        <FocusVsDistraction
          data={focusVsDistractionData}
          isLoading={isFetchingStats}
        />
        <RecentAlerts
          data={recentAlertsData}
          isLoading={isFetchingAlerts}
          reportFiltersSearch={alertsReportSearch}
        />
        <AttendanceSummary
          data={attendanceSummaryData}
          isLoading={isFetchingStats}
          reportFiltersSearch={attendanceReportSearch}
          className={styles.quarterCard}
        />
      </div>
    </div>
  );
};
